import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import APIRequest, { APIError } from 'system/APIRequest';

/* Example usage from another component:
import { usePDFExport } from 'utils/usePDFExport';
---
const { downloadPDF } = usePDFExport();
<button onClick={() => downloadPDF(myRef, { filename: 'custom.pdf' })}>
  Export PDF
</button>
*/

export interface PDFExportOptions {
  filename?: string;
}

export function usePDFExport() {
  const { i18n } = useTranslation();

  const downloadPDF = useCallback(
    (widgetRef: React.RefObject<HTMLDivElement>, options?: PDFExportOptions): Promise<void> => {
      // Check if we have a valid reference to the widget content
      if (!widgetRef?.current) {
        enqueueSnackbar(i18n.t('widgets.download-pdf-failure', { errorMessage: 'No content to export' }), {
          variant: 'error',
          persist: true,
        });
        return Promise.resolve();
      }

      // Extract styles from all stylesheets in the document
      function extractAllStyles() {
        const styles:string[] = [];

        Array.from(document.styleSheets).forEach((sheet) => {
          try {
            const rules = Array.from(sheet.cssRules || []);
            if (rules.length > 0) {
              const css = rules.map(rule => rule.cssText).join('\n');
              styles.push(`<style>${css}</style>`);
            }
          } catch (e) {
            if(sheet.href) {
              styles.push(`<link rel="stylesheet" href="${sheet.href}">`);
            }
          }
        });

        return styles.join('\n');
      }

      // Custom styles for PDF export, overriding for compatibility
      const customStyle = `
        <style>
          html * { font-family: Arial, Helvetica, sans-serif !important; }
        </style>
      `;

      // Clone the widget HTML for export to avoid modifying the live DOM
      const widgetHTML = widgetRef.current.cloneNode(true) as HTMLElement;
      // Remove box shadows from MUI Paper elements for PDF export
      widgetHTML.querySelectorAll<HTMLElement>('div.MuiPaper-root').forEach(el => {
        el.style.setProperty('box-shadow', 'none', 'important');
      });

      // Construct the full HTML document for PDF rendering
      const defaultHTML = `
        <!DOCTYPE html>
        <html lang="en">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Widget Export</title>
            ${extractAllStyles()}
            ${customStyle}
          </head>
          <body>
            ${widgetHTML.innerHTML}
          </body>
        </html>
      `;

      // Send the HTML to the server to convert to PDF
      return APIRequest(`pdf-render`, {}, {
        method: 'POST',
        body: defaultHTML,
      })
        .then(response => {
          const body = response.body;
          const reader = body.getReader();
          const responseChunks: Uint8Array<ArrayBuffer>[] = [];

          // Read the response stream chunk by chunk until done, then create a Blob and trigger download
          function readResponseStream(): Promise<void> {
            return reader.read().then(({ done, value }: { done: boolean; value: Uint8Array<ArrayBuffer> }) => {
              if (done) {
                // All done, create the blob and download
                const filename = options?.filename || 'widget-export.pdf';
                const pdfBlob = new Blob(responseChunks, { type: 'application/pdf' });
                const url = window.URL.createObjectURL(pdfBlob);

                const tempLink = document.createElement('a');
                tempLink.href = url;
                tempLink.setAttribute('download', filename);
                document.body.appendChild(tempLink);
                tempLink.click();

                document.body.removeChild(tempLink);
                window.URL.revokeObjectURL(url);

                enqueueSnackbar(i18n.t('widgets.download-pdf-success'), { variant: 'success' });
                return Promise.resolve();
              } else {
                // Append the chunk to the array and read the next chunk
                responseChunks.push(value);
                return readResponseStream();
              }
            });
          }
          return readResponseStream();
        })
        .catch((reason: APIError) => {
          enqueueSnackbar(
            i18n.t('widgets.download-pdf-failure', { errorMessage: reason.message }),
            { variant: 'error', persist: true }
          );
        });
    },
    [i18n]
  );

  return { downloadPDF } as const;
}
